import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Fab,
  useTheme,
  Divider,
  Grid,
  Avatar,
  CardActionArea
} from '@mui/material';
import { Add, EventBusy, CalendarMonth, AccessTime, FlightTakeoff, Sick, WorkOff, QuestionMark } from '@mui/icons-material';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';

const LeaveRequest = ({ employeeId = 1 }) => {
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/worker/leave-requests?employee_id=${employeeId}`);
      setRequests(response.data || []);
    } catch (err) {
      console.error('Failed to load leave requests:', err);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/worker/leave-requests', {
        employee_id: employeeId,
        ...formData
      });
      setDialogOpen(false);
      setFormData({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      await loadRequests();
    } catch (err) {
      setError('Failed to submit leave request. Please try again.');
    }
    setSubmitting(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getLeaveIcon = (type) => {
    switch (type) {
        case 'annual': return <FlightTakeoff fontSize="small" />;
        case 'sick': return <Sick fontSize="small" />;
        case 'unpaid': return <WorkOff fontSize="small" />;
        default: return <QuestionMark fontSize="small" />;
    }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    try {
      return differenceInBusinessDays(parseISO(end), parseISO(start)) + 1;
    } catch (e) {
      return 0;
    }
  };

  if (loading) {
    return <LoadingState message="Loading leave requests..." />;
  }

  return (
    <Box sx={{ pb: 10 }}>
        {/* Header */}
       <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="text.primary">Leave Requests</Typography>
        <Typography variant="body2" color="text.secondary">
            Manage your time off and view request status
        </Typography>
      </Box>
      
      {requests.length === 0 ? (
        <Paper 
            elevation={0}
            sx={{ 
                p: 2, 
                borderRadius: 3, 
                mt: 4, 
                bgcolor: '#F8FAFC',
                border: '1px dashed #E2E8F0',
                overflow: 'hidden'
            }} 
        >
          <EmptyState 
            icon={<CalendarMonth />}
            title="No pending leave requests"
            description="You haven't submitted any leave requests yet."
            actionLabel="Request Time Off"
            onAction={() => setDialogOpen(true)}
          />
        </Paper>
      ) : (
        <Box>
          {requests.map((req) => (
            <Card 
              key={req.id} 
              elevation={0}
              sx={{ 
                mb: 2, 
                borderRadius: 3,
                border: '1px solid #E2E8F0',
                borderLeft: `5px solid ${theme.palette[getStatusColor(req.status)].main}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }
              }} 
            >
              <CardContent sx={{ pb: 2, p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 40, height: 40 }}>
                        {getLeaveIcon(req.leave_type)}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}>
                        {req.leave_type} Leave
                        </Typography>
                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                            {format(parseISO(req.start_date), 'MMM d, yyyy')} — {format(parseISO(req.end_date), 'MMM d, yyyy')}
                        </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={req.status}
                    color={getStatusColor(req.status)}
                    size="small"
                    variant={req.status === 'pending' ? 'outlined' : 'filled'}
                    sx={{ fontWeight: 700, textTransform: 'capitalize', borderRadius: 1 }}
                  />
                </Box>
                
                <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#F8FAFC', px: 1.5, py: 0.5, borderRadius: 1 }}>
                        <AccessTime fontSize="small" color="action" sx={{ fontSize: 16 }} />
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {calculateDuration(req.start_date, req.end_date)} day{calculateDuration(req.start_date, req.end_date) !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    {req.reason && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', maxWidth: '60%', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            "{req.reason}"
                        </Typography>
                    )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* FAB for New Request */}
      {requests.length > 0 && (
        <Fab
            color="primary"
            sx={{ 
                position: 'fixed', 
                bottom: 80, 
                right: 24,
                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)'
            }}
            onClick={() => setDialogOpen(true)}
        >
            <Add />
        </Fab>
      )}

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Request Time Off</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              select
              fullWidth
              label="Leave Type"
              value={formData.leave_type}
              onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
              SelectProps={{ native: true }}
              variant="outlined"
            >
              <option value="annual">Annual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="unpaid">Unpaid Leave</option>
              <option value="other">Other</option>
            </TextField>
            
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
            
            {formData.start_date && formData.end_date && (
                <Alert severity="info" icon={<AccessTime fontSize="inherit" />} sx={{ py: 0, borderRadius: 2 }}>
                    Duration: <strong>{calculateDuration(formData.start_date, formData.end_date)} working days</strong>
                </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason (optional)"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Briefly describe why you need time off..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.start_date || !formData.end_date}
            sx={{ fontWeight: 600 }}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveRequest;
